import { useCallback, useEffect, useRef, useState } from "react";
import { GuestAccessTokenResponse } from "../services/guestAccessTokenService";

const RENEWAL_SKEW_MS = 30_000;
const MIN_RENEWAL_DELAY_MS = 1_000;
const MINT_RETRY_BASE_DELAY_MS = 1_000;
export const GUEST_TOKEN_MINT_MAX_ATTEMPTS = 3;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const GUEST_TOKEN_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;
const GUEST_TOKEN_RELOAD_TIMESTAMP_KEY = "guestTokenExpiryReloadedAt";

export const isValidGuestEmail = (email?: string | null): email is string =>
  Boolean(email && EMAIL_PATTERN.test(email.trim()));

export const getGuestTokenRenewalDelay = (expiresInSeconds: number): number =>
  Math.max(MIN_RENEWAL_DELAY_MS, expiresInSeconds * 1000 - RENEWAL_SKEW_MS);

export const getGuestTokenMintRetryDelay = (failedAttempt: number): number =>
  MINT_RETRY_BASE_DELAY_MS * 2 ** Math.max(0, failedAttempt - 1);

export const shouldReloadForGuestTokenExpiry = (
  lastReloadedAt: string | null,
  now = Date.now()
): boolean => {
  if (!lastReloadedAt) {
    return true;
  }
  const timestamp = Number(lastReloadedAt);
  return (
    !Number.isFinite(timestamp) ||
    now - timestamp >= GUEST_TOKEN_RELOAD_COOLDOWN_MS
  );
};

const containsUnauthorizedStatus = (
  value: unknown,
  seen = new Set<object>()
): boolean => {
  if (value === 401 || value === "401") {
    return true;
  }
  if (!value || typeof value !== "object" || seen.has(value)) {
    return false;
  }
  seen.add(value);
  return Object.entries(value as Record<string, unknown>).some(([key, item]) => {
    if (
      ["status", "statusCode", "httpStatus", "http_status"].includes(key) &&
      (item === 401 || item === "401")
    ) {
      return true;
    }
    return containsUnauthorizedStatus(item, seen);
  });
};

export const isGuestTokenExpiredEvent = (event: unknown): boolean => {
  const detail =
    event && typeof event === "object" && "detail" in event
      ? (event as { detail?: unknown }).detail
      : event;
  if (containsUnauthorizedStatus(detail)) {
    return true;
  }
  if (!detail || typeof detail !== "object") {
    return detail === "TOKEN_EXPIRED";
  }
  const data = detail as Record<string, unknown>;
  return [data.message, data.code, data.type, data.error].some(
    (value) => value === "TOKEN_EXPIRED"
  );
};

interface UseGuestAccessTokenOptions {
  enabled: boolean;
  email?: string | null;
  mintToken: (email: string) => Promise<GuestAccessTokenResponse>;
}

export interface GuestAccessTokenLifecycle {
  token: string | null;
  error: boolean;
  isMinting: boolean;
  renew: () => void;
  retry: () => void;
  handleUcpGlobalEvent: (event: unknown) => void;
}

export const useGuestAccessToken = ({
  enabled,
  email,
  mintToken,
}: UseGuestAccessTokenOptions): GuestAccessTokenLifecycle => {
  const normalizedEmail = email?.trim() ?? "";
  const validEmail = isValidGuestEmail(normalizedEmail);
  const mintTokenRef = useRef(mintToken);
  const timerRef = useRef<number>();
  const requestIdRef = useRef(0);
  const mintingRef = useRef(false);
  const [token, setToken] = useState<string | null>(null);
  const [tokenEmail, setTokenEmail] = useState("");
  const [error, setError] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [renewalRequest, setRenewalRequest] = useState(0);

  useEffect(() => {
    mintTokenRef.current = mintToken;
  }, [mintToken]);

  const renew = useCallback(() => {
    if (enabled && validEmail && !mintingRef.current) {
      setRenewalRequest((value) => value + 1);
    }
  }, [enabled, validEmail]);

  const retry = useCallback(() => {
    setError(false);
    setToken(null);
    setTokenEmail("");
    renew();
  }, [renew]);

  const handleUcpGlobalEvent = useCallback((event: unknown) => {
    const now = Date.now();
    if (
      isGuestTokenExpiredEvent(event) &&
      shouldReloadForGuestTokenExpiry(
        sessionStorage.getItem(GUEST_TOKEN_RELOAD_TIMESTAMP_KEY),
        now
      )
    ) {
      sessionStorage.setItem(GUEST_TOKEN_RELOAD_TIMESTAMP_KEY, String(now));
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    window.clearTimeout(timerRef.current);

    if (!enabled || !validEmail) {
      requestIdRef.current += 1;
      mintingRef.current = false;
      setToken(null);
      setTokenEmail("");
      setError(false);
      setIsMinting(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;
    mintingRef.current = true;
    setIsMinting(true);
    setError(false);

    function isCurrentRequest(): boolean {
      return !cancelled && requestId === requestIdRef.current;
    }

    function finishMinting(): void {
      if (isCurrentRequest()) {
        mintingRef.current = false;
        setIsMinting(false);
      }
    }

    function retryOrFail(failedAttempt: number): void {
      if (!isCurrentRequest()) {
        return;
      }
      if (failedAttempt < GUEST_TOKEN_MINT_MAX_ATTEMPTS) {
        timerRef.current = window.setTimeout(
          () => attemptMint(failedAttempt + 1),
          getGuestTokenMintRetryDelay(failedAttempt)
        );
        return;
      }
      setError(true);
      finishMinting();
    }

    function attemptMint(attempt: number): void {
      Promise.resolve()
        .then(() => mintTokenRef.current(normalizedEmail))
        .then((result) => {
          if (!isCurrentRequest()) {
            return;
          }
          if (
            !result.access_token ||
            !Number.isFinite(result.expires_in) ||
            result.expires_in <= 0
          ) {
            retryOrFail(attempt);
            return;
          }
          setToken(result.access_token);
          setTokenEmail(normalizedEmail);
          timerRef.current = window.setTimeout(
            renew,
            getGuestTokenRenewalDelay(result.expires_in)
          );
          finishMinting();
        })
        .catch(() => retryOrFail(attempt));
    }

    attemptMint(1);

    return () => {
      cancelled = true;
      window.clearTimeout(timerRef.current);
    };
  }, [enabled, normalizedEmail, renewalRequest, renew, validEmail]);

  return {
    token: enabled && tokenEmail === normalizedEmail ? token : null,
    error,
    isMinting,
    renew,
    retry,
    handleUcpGlobalEvent,
  };
};
